<template>
  <input-base
    :overlap="overlap"
    :small="small"
    :transparent="transparent"
    :stretched="stretched"
    :slimmer="slimmer"
    v-click-outside="hideTooltip"
    unPositioned>

    <div
      class="date-range-input"
      slot="input">

      <q-icon
        :allowSelfUpdate="false"
        class="dr-icon"
        height="27px"
        name="calendar" />

      <date-range-picker
        :class="`${getInnerStyle} ${getExtraStyle}`"
        :name="inputName"
        :months="months"
        :shortDays="shortDays"
        :captions="captions"
        :presetRanges="getPickerPresets"
        :ref="inputName"
        @selected="drSelected" />

      <div class="date-range-placeholder">
        {{ $t(getPlaceholder) }}
      </div>

    </div>
  </input-base>
</template>

<script>
import VueRangedatePicker from 'vue-rangedate-picker'
import '@/assets/styles/dateRange.css'

import ClickOutside from 'vue-click-outside'

import InputBase from '@/components/standard/input/InputBase'
import QIcon from '@/components/standard/display/QIcon'
import stylers from '@/mixins/components/standard/input/inputButton/stylers'
import '@/assets/styles/standardInputs.css'
import drpg from '@/assets/modules/inputs/dateRangePresetGenerator'

import moment from 'moment'

export default {
  data () {
    return {
      months: moment.months(),
      shortDays: moment.weekdaysShort(),
      captions: {
        title: '',
        ok_button: this.$t('Button.confirm')
      },
      // Default coppied; changed labels!
      presetRanges: drpg.generate([
        'today',
        'thisMonth',
        'lastMonth',
        'last7days',
        'last30days'
      ],
      {
        'today': this.$t('DateRange.preset.today'),
        'thisMonth': this.$t('DateRange.preset.thisMonth'),
        'lastMonth': this.$t('DateRange.preset.lastMonth'),
        'last7days': this.$t('DateRange.preset.last7days'),
        'last30days': this.$t('DateRange.preset.last30days')
      }),

      value: prepValue(this.inputValue),

      defaultPlaceholder: 'Input.DateRange.placeholder.default'
    }
  },

  methods: {
    /**
     * Hides date range picker's tooltip
     */
    hideTooltip (e) {
      // Close it only if it is shown!
      if (!this.$refs[this.inputName].isOpen) return
      this.$refs[this.inputName].toggleCalendar()
    },

    /**
     * Selects range
     * @param {Object} e Selected range
     */
    drSelected (e) {
      let { start: from, end: to, start: id } = e

      // If end date is not specified, use start date
      to = to || from

      from = moment(from).add(-1, 'day').startOf('day').toISOString()
      to = moment(to).add(-1, 'day').endOf('day').toISOString()
      id = moment(id).add(-1, 'day').startOf('day').toISOString()

      let evt = {
        from,
        to,
        id
      }

      this.$set(this, 'value', evt)
      this.$emit('fieldUpdate', evt)
    }
  },

  computed: {
    /**
     * Gives input's placeholder
     * @returns {String}
     */
    getPlaceholder () {
      let { from, to } = this.inputValue
      if (!from && !to) return this.defaultPlaceholder
      let rtr = ''
      if (from) rtr += moment(from).format('DD. MM')
      if (from && to) rtr += ' - '
      if (to) rtr += moment(to).format('DD. MM. YYYY')
      return rtr
    },

    /**
     * Gives input's extra styles
     * @returns {String}
     */
    getExtraStyle () {
      let base = `date-range-picker q-arrow`
      if (this.boldPholder) base += ' dt-bold'

      return base
    },

    getPickerPresets () {
      return this.dateRangePickerPresets || this.presetRanges
    }
  },

  components: {
    'input-base': InputBase,
    'date-range-picker': VueRangedatePicker,
    'q-icon': QIcon
  },
  mixins: [
    stylers
  ],
  props: {
    dateRangePickerPresets: {
      type: Object,
      required: false,
      default: null
    },
    inputValue: {
      type: Object,
      required: false,
      default: () => { return {} }
    },
    boldPholder: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  directives: {
    ClickOutside
  }
}

/**
 * Prepares value for DateRange component
 * @param {Object} val
 * @returns {Object}
 */
function prepValue (val = {}) {
  let { from: start, to: end } = val
  start = new Date(start)
  end = new Date(end)

  return {
    start,
    end
  }
}

</script>

<style>
.dt-bold .vdatetime-input {
  font-weight: bold;
}

.label-icon {
  top: -1px;
  position: relative;
}

.dtime-icon {
  position: absolute;
  margin-top: 15px;
  margin-left: 18px;
}
.dt-scroller {
  overflow: hidden;
}
.vdatetime-input {
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  padding-left: 31px;
}

@media (max-width: 768px) {
  .vdatetime-input {
    padding-left: 0;
    width: 168px;
  }
}
</style>

<style scoped>
.standard-outer-input-style {
  position: unset!important;
}
.dt-picker {
  border: 1px solid rgb(194, 194, 194);
  padding: 5px 15px;
}
.rounded {
  border-radius: 10px;
}

.p-holder {
  position: absolute;
  margin-top: auto;
  font-size: 14px;
  transition: margin-top .3s, font-size .3s;
}
.above {
  margin-top: -15px;
  font-size: 10px;
}
.small {
  padding-top: 1px!important;
  padding-bottom: 1px!important;
  height: 27px!important;
}
.smaller {
  padding-top: 4px!important;
  padding-bottom: 4px!important;
  height: 32px!important;
}
.dtime-icon.adjust-small {
  margin-top: 4px;
}

.adjust-smaller {
  margin-top: 8px;
  margin-left: 12px;
}

.date-range-input {
  display: flex;
  align-items: center;
}

.date-range-input .dr-icon {
  position: absolute;
  width: 20px;
  height: 20px;
}

.date-range-placeholder {
  position: absolute;
  margin-left: 27px;
  font-size: 12px;
  font-weight: lighter;
  line-height: 14px;
  margin-top: 2px;
}

.standard-inner-input-style {
  border: none;
}

</style>



// WEBPACK FOOTER //
// src/components/standard/input/InputDateRange.vue?85296668